import { useCallback, useEffect, useState } from 'react';

import { UseFormReturn, useWatch } from 'react-hook-form';

import useDesks from '@/hooks/useDesks';
import { fetchWhatsAppTemplates } from '@/modules/whatsapp/hooks/useWhatsAppTemplates';
import { WhatsappTemplate } from '@/shared/types/broadcast';
import { Integration, IntegrationStatus, IntegrationType } from '@/shared/types/integration';

import { CampaignForm, DEFAULT_NO_RESPONSE_WAIT_MINUTES } from '../../utils';

/**
 * Custom hook that handles all business logic for the campaign build form
 * @param methods - React Hook Form methods
 * @returns Object containing state, handlers, and derived data
 */
export function useCampaignBuildLogic(methods: UseFormReturn<CampaignForm>) {
  const { desks, getAllIntegrations } = useDesks();
  const { control, setValue, getValues } = methods;

  // State
  const [availableIntegrations, setAvailableIntegrations] = useState<Partial<Integration>[]>([]);
  // Templates stored per integration ID to support multiple WhatsApp senders
  const [whatsappTemplatesMap, setWhatsappTemplatesMap] = useState<
    Record<string, WhatsappTemplate[]>
  >({});
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Watched values
  const primarySender = useWatch({ control, name: 'primarySender' });
  const fallbackSenders = useWatch({ control, name: 'fallbackSenders' });
  const fallbackIfNotDelivered = useWatch({ control, name: 'fallbackIfNotDelivered' });

  // Derived data - all integrations are available for both primary and fallback
  const allIntegrations = availableIntegrations.filter(
    (integration) =>
      integration.type === IntegrationType.WHATSAPP ||
      integration.type === IntegrationType.INFOBIP_SMS ||
      integration.type === IntegrationType.INFOBIP_RCS ||
      integration.type === IntegrationType.MSTAT_VIBER ||
      integration.type === IntegrationType.EMAIL ||
      integration.type === IntegrationType.TWILIO_VOICE ||
      integration.type === IntegrationType.TELNYX_VOICE ||
      integration.type === IntegrationType.AGORA_VOICE
  );

  /**
   * Load templates for a specific WhatsApp integration if not already loaded
   */
  const loadTemplatesForIntegration = useCallback(
    async (integrationId: string, integration: Partial<Integration>) => {
      // Only load for WhatsApp integrations
      if (integration.type !== IntegrationType.WHATSAPP) {
        return;
      }

      // Skip if already loaded
      if (whatsappTemplatesMap[integrationId]) {
        return;
      }

      if (!integration.desk_id) {
        return;
      }

      setLoadingTemplates(true);
      try {
        const response = await fetchWhatsAppTemplates(integration.desk_id, integrationId);
        setWhatsappTemplatesMap((prev) => ({ ...prev, [integrationId]: response.templates || [] }));
      } catch (error) {
        console.error('Error loading templates:', error);
        setWhatsappTemplatesMap((prev) => ({ ...prev, [integrationId]: [] }));
      } finally {
        setLoadingTemplates(false);
      }
    },
    [whatsappTemplatesMap]
  );

  // Handlers
  const onPrimarySenderChange = useCallback(
    (integrationId: string) => {
      const integration = allIntegrations.find((i) => i.integration_id === integrationId);
      if (integration) {
        // Clear all fields when switching integration type
        setValue('primarySender', {
          integrationId,
          integrationType: integration.type as IntegrationType,
          content: '',
          subject: '',
          template: undefined,
          headerParameters: [],
          bodyParameters: [],
          footerParameters: [],
          headerMediaParam: undefined,
        });

        // Load templates directly when WhatsApp is selected
        loadTemplatesForIntegration(integrationId, integration);
      }
    },
    [allIntegrations, setValue, loadTemplatesForIntegration]
  );

  const onFallbackSenderChange = useCallback(
    (integrationId: string) => {
      const integration = allIntegrations.find((i) => i.integration_id === integrationId);
      if (integration) {
        const currentFallbackSenders = getValues('fallbackSenders') || [];

        // Clear all fields when switching integration type
        const updatedFallbackSenders = [...currentFallbackSenders];
        updatedFallbackSenders[0] = {
          integrationId,
          integrationType: integration.type as IntegrationType,
          content: '',
          subject: '',
          template: undefined,
          waitTimeMinutes: 0,
          headerParameters: [],
          bodyParameters: [],
          footerParameters: [],
          headerMediaParam: undefined,
        };

        setValue('fallbackSenders', updatedFallbackSenders);

        // Load templates directly when WhatsApp is selected
        loadTemplatesForIntegration(integrationId, integration);
      }
    },
    [allIntegrations, getValues, setValue, loadTemplatesForIntegration]
  );

  const onNoResponseSenderChange = useCallback(
    (integrationId: string, senderIndex: number) => {
      const integration = allIntegrations.find((i) => i.integration_id === integrationId);
      if (integration) {
        const currentFallbackSenders = getValues('fallbackSenders') || [];

        // Clear all fields when switching integration type
        const updatedFallbackSenders = [...currentFallbackSenders];
        updatedFallbackSenders[senderIndex] = {
          integrationId,
          integrationType: integration.type as IntegrationType,
          content: '',
          subject: '',
          template: undefined,
          waitTimeMinutes:
            updatedFallbackSenders[senderIndex]?.waitTimeMinutes ||
            DEFAULT_NO_RESPONSE_WAIT_MINUTES,
          headerParameters: [],
          bodyParameters: [],
          footerParameters: [],
          headerMediaParam: undefined,
        };

        setValue('fallbackSenders', updatedFallbackSenders);

        // Load templates directly when WhatsApp is selected
        loadTemplatesForIntegration(integrationId, integration);
      }
    },
    [allIntegrations, getValues, setValue, loadTemplatesForIntegration]
  );

  // Load integrations on component mount
  useEffect(() => {
    const loadIntegrations = async () => {
      if (!desks?.length) {
        return;
      }

      setLoadingIntegrations(true);
      try {
        // Load all integrations in parallel - use allSettled so one failure doesn't break all
        const results = await Promise.allSettled([
          getAllIntegrations(IntegrationType.WHATSAPP),
          getAllIntegrations(IntegrationType.INFOBIP_SMS),
          getAllIntegrations(IntegrationType.INFOBIP_RCS),
          getAllIntegrations(IntegrationType.MSTAT_VIBER),
          getAllIntegrations(IntegrationType.EMAIL),
          getAllIntegrations(IntegrationType.TWILIO_VOICE),
          getAllIntegrations(IntegrationType.TELNYX_VOICE),
          getAllIntegrations(IntegrationType.AGORA_VOICE),
        ]);

        // Extract successful results, defaulting to empty array for failures
        const [
          whatsappIntegrations,
          smsIntegrations,
          rcsIntegrations,
          mstatViberIntegrations,
          emailIntegrations,
          twilioVoiceIntegrations,
          telnyxVoiceIntegrations,
          agoraVoiceIntegrations,
        ] = results.map((result) => (result.status === 'fulfilled' ? result.value : []));

        const allIntegrations = [
          ...whatsappIntegrations.map((integration) => ({
            integration_id: integration.integration_id,
            type: integration.type,
            name: `${integration.name} ${(integration.config as { display_phone_number?: string; phone_number_id?: string })?.display_phone_number || (integration.config as { phone_number_id?: string })?.phone_number_id || '-'}`,
            desk_id: integration.desk_id,
            status: integration.status,
            active: integration.active,
          })),
          ...smsIntegrations.map((integration) => ({
            integration_id: integration.integration_id,
            type: integration.type,
            name: `${integration.name} +${(integration.config as { number?: string })?.number || '-'}`,
            desk_id: integration.desk_id,
            status: integration.status,
            active: integration.active,
          })),
          ...rcsIntegrations.map((integration) => ({
            integration_id: integration.integration_id,
            type: integration.type,
            name: `${integration.name} · ${(integration.config as { sender_name?: string })?.sender_name || '-'}`,
            desk_id: integration.desk_id,
            status: integration.status,
            active: integration.active,
          })),
          ...mstatViberIntegrations.map((integration) => ({
            integration_id: integration.integration_id,
            type: integration.type,
            name: `${integration.name} · ${(integration.config as { account_id?: number })?.account_id || '-'}`,
            desk_id: integration.desk_id,
            status: integration.status,
            active: integration.active,
          })),
          ...emailIntegrations.map((integration) => ({
            integration_id: integration.integration_id,
            type: integration.type,
            name: `${integration.name} · ${integration.config.from_email}`,
            desk_id: integration.desk_id,
            status: integration.status,
            active: integration.active,
          })),
          ...twilioVoiceIntegrations
            .filter((integration) => {
              // Only include Twilio Voice integrations that have a phone number
              const config = integration.config as { phone_number?: string };
              return config?.phone_number && config.phone_number.trim() !== '';
            })
            .map((integration) => ({
              integration_id: integration.integration_id,
              type: integration.type,
              name: `${integration.name} ${(integration.config as { phone_number?: string })?.phone_number || '-'}`,
              desk_id: integration.desk_id,
              status: integration.status,
              active: integration.active,
              config: integration.config,
            })),
          ...telnyxVoiceIntegrations
            .filter((integration) => {
              // Only include Telnyx Voice integrations that have a phone number
              const config = integration.config as { phone_number?: string };
              return config?.phone_number && config.phone_number.trim() !== '';
            })
            .map((integration) => ({
              integration_id: integration.integration_id,
              type: integration.type,
              name: `${integration.name} ${(integration.config as { phone_number?: string })?.phone_number || '-'}`,
              desk_id: integration.desk_id,
              status: integration.status,
              active: integration.active,
              config: integration.config,
            })),
          ...agoraVoiceIntegrations
            .filter((integration) => {
              // Only include Agora Voice integrations that have a phone number
              const config = integration.config as { phone_number?: string };
              return config?.phone_number && config.phone_number.trim() !== '';
            })
            .map((integration) => ({
              integration_id: integration.integration_id,
              type: integration.type,
              name: `${integration.name} ${(integration.config as { phone_number?: string })?.phone_number || '-'}`,
              desk_id: integration.desk_id,
              status: integration.status,
              active: integration.active,
              config: integration.config,
            })),
        ].filter(
          (integration) =>
            // integrations-controller has no agora-voice verifier yet, so its status
            // never reaches verified; gate it on active only until that ships.
            (integration.status === IntegrationStatus.VERIFIED ||
              integration.type === IntegrationType.AGORA_VOICE) &&
            integration.active === true
        );
        setAvailableIntegrations(allIntegrations);

        // Load templates for any pre-selected WhatsApp integrations (when editing existing campaign)
        const formPrimarySender = getValues('primarySender');
        const formFallbackSenders = getValues('fallbackSenders');

        const whatsappIntegrationIds = new Set<string>();

        if (
          formPrimarySender?.integrationType === IntegrationType.WHATSAPP &&
          formPrimarySender.integrationId
        ) {
          whatsappIntegrationIds.add(formPrimarySender.integrationId);
        }

        formFallbackSenders?.forEach((sender) => {
          if (sender?.integrationType === IntegrationType.WHATSAPP && sender.integrationId) {
            whatsappIntegrationIds.add(sender.integrationId);
          }
        });

        // Load templates for all pre-selected WhatsApp integrations
        if (whatsappIntegrationIds.size > 0) {
          setLoadingTemplates(true);
          try {
            const templatePromises = Array.from(whatsappIntegrationIds).map(
              async (integrationId) => {
                const integration = allIntegrations.find((i) => i.integration_id === integrationId);
                if (!integration?.desk_id) {
                  return { integrationId, templates: [] as WhatsappTemplate[] };
                }

                try {
                  const response = await fetchWhatsAppTemplates(integration.desk_id, integrationId);
                  return {
                    integrationId,
                    templates: (response.templates || []) as WhatsappTemplate[],
                  };
                } catch {
                  return { integrationId, templates: [] as WhatsappTemplate[] };
                }
              }
            );

            const results = await Promise.all(templatePromises);
            const templatesMap: Record<string, WhatsappTemplate[]> = {};
            results.forEach(({ integrationId, templates }) => {
              templatesMap[integrationId] = templates;
            });
            setWhatsappTemplatesMap(templatesMap);
          } catch (error) {
            console.error('Error loading templates:', error);
          } finally {
            setLoadingTemplates(false);
          }
        }
      } catch (error) {
        console.error('Error loading integrations:', error);
      } finally {
        setLoadingIntegrations(false);
      }
    };

    loadIntegrations();
  }, [desks, getAllIntegrations, setValue, getValues]);

  // Helper function to get templates for a specific integration
  const getTemplatesForIntegration = (integrationId?: string): WhatsappTemplate[] => {
    if (!integrationId) {
      return [];
    }
    return whatsappTemplatesMap[integrationId] || [];
  };

  return {
    // State
    whatsappTemplatesMap,
    getTemplatesForIntegration,
    loadingIntegrations,
    loadingTemplates,

    // Derived data
    primarySender,
    fallbackSenders,
    fallbackIfNotDelivered,
    allIntegrations,

    // Handlers
    onPrimarySenderChange,
    onFallbackSenderChange,
    onNoResponseSenderChange,
  };
}
